/**
 * Chat View Component
 * 
 * Main chat interface showing messages and input for active conversation.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Typography, Avatar, Empty, Spin, Button, Dropdown } from 'antd';
import { TeamOutlined, UserAddOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, MoreOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Conversation, Message, ChatScope } from '@municipality/types';
import { MessageItem } from './MessageItem';
import { MessageInput } from './MessageInput';
import { ParticipantsModal } from './ParticipantsModal';
import { AddMembersModal } from './AddMembersModal';
import { RenameGroupChatModal } from './RenameGroupChatModal';
import { DeleteChatConfirmationModal } from './DeleteChatConfirmationModal';
import { EditMessageModal } from './EditMessageModal';
import { useChatStore, chatService } from '@municipality/chat';
import { format, isSameDay } from 'date-fns';
import { useResponsive } from '@municipality/ui';

const { Title, Text } = Typography;

/**
 * Get initials from a name (first letter of first and last name)
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string (name)
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

interface ChatViewProps {
  conversation: Conversation | null;
  messages: Message[];
  currentUserId: string;
  isLoading: boolean;
  onSendMessage: (content: string, mentions?: string[], replyTo?: string) => void;
  onTyping: (isTyping: boolean) => void;
  scope?: ChatScope;
  municipalityId?: string;
  onBack?: () => void;
}

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
`;
const ChatHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $isMobile }) => ($isMobile ? '8px 16px' : '12px 24px')};
  border-bottom: 1px solid #f0f0f0;
  background-color: ${({ $isMobile }) => ($isMobile ? '#f5f5f5' : '#fff')};
  flex-shrink: 0;
  min-height: ${({ $isMobile }) => ($isMobile ? 'auto' : 'auto')};
`;

const BackButton = styled(Button)`
  margin-right: 8px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const HeaderContent = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  margin-left: ${({ $isMobile }) => ($isMobile ? '0' : '12px')};
  flex: 1;
  min-width: 0;
`;

const HeaderRight = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  align-items: ${({ $isMobile }) => ($isMobile ? 'flex-end' : 'center')};
  gap: ${({ $isMobile }) => ($isMobile ? '4px' : '8px')};
  justify-content: ${({ $isMobile }) => ($isMobile ? 'flex-start' : 'flex-end')};
`;

const MobileMenuButton = styled(Button)`
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const ConversationNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConversationName = styled(Title, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile?: boolean }>`
  margin: 0 !important;
  font-size: ${({ $isMobile }) => ($isMobile ? '14px' : '16px')} !important;
  line-height: ${({ $isMobile }) => ($isMobile ? '1.2' : '1.5')} !important;
`;

const EditIconButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  padding: 0;
  width: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  height: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  min-width: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  min-height: ${({ $isMobile }) => ($isMobile ? '44px' : '20px')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: color 0.2s;

  &:hover {
    color: #1890ff;
  }
`;

const OnlineStatus = styled(Text, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  font-size: ${({ $isMobile }) => ($isMobile ? '11px' : '12px')};
  color: #999;
`;

const ClickableParticipantCount = styled(Text, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  font-size: ${({ $isMobile }) => ($isMobile ? '11px' : '12px')};
  color: #999;
  cursor: pointer;
  transition: color 0.2s;
  display: ${({ $isMobile }) => ($isMobile ? 'block' : 'block')};
  line-height: ${({ $isMobile }) => ($isMobile ? '1.2' : '1.5')};
  margin-top: ${({ $isMobile }) => ($isMobile ? '2px' : '0')};

  &:hover {
    color: #1890ff;
    text-decoration: underline;
  }
`;

const MessagesContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile' && prop !== '$isTablet',
})<{ $isTablet: boolean; $isMobile: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${({ $isTablet, $isMobile }) => {
    if ($isMobile) return '12px';
    if ($isTablet) return '16px';
    return '24px';
  }};
  background-color: #fff;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
`;

const TypingIndicator = styled.div`
  padding: 8px 24px;
  font-size: 13px;
  color: #666;
  font-style: italic;
  background-color: #fff;
  min-height: 0;
  height: auto;
`;

const HeaderAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor' && prop !== '$isMobile' && prop !== 'size',
})<{ $bgColor?: string; $isMobile: boolean }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: ${({ $isMobile }) => ($isMobile ? '11px' : '13px')} !important;
  flex-shrink: 0;
  width: ${({ $isMobile }) => ($isMobile ? '28px' : '30px')} !important;
  height: ${({ $isMobile }) => ($isMobile ? '28px' : '30px')} !important;
  min-width: ${({ $isMobile }) => ($isMobile ? '28px' : '30px')} !important;
`;

const DateDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  text-align: center;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #e0e0e0;
  }
`;

const DateDividerText = styled(Text)`
  padding: 0 16px;
  font-size: 12px;
  color: #999;
  font-weight: 500;
  white-space: nowrap;
`;

export const ChatView: React.FC<ChatViewProps> = ({
  conversation,
  messages,
  currentUserId,
  isLoading,
  onSendMessage,
  onTyping,
  scope = 'municipality',
  municipalityId,
  onBack,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef<number>(0);
  const isUserScrollingRef = useRef<boolean>(false);
  const scrollPositionRef = useRef<number>(0);
  const { t } = useTranslation('chat');
  const { isTablet, isMobile } = useResponsive();
  const markAsRead = useChatStore((state) => state.markAsRead);
  const reactToMessage = useChatStore((state) => state.reactToMessage);
  const removeReaction = useChatStore((state) => state.removeReaction);
  const renameGroupChat = useChatStore((state) => state.renameGroupChat);
  const deleteConversation = useChatStore((state) => state.deleteConversation);
  const editMessage = useChatStore((state) => state.editMessage);
  const prependMessages = useChatStore((state) => state.prependMessages);
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [addMembersModalOpen, setAddMembersModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState<Message | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [enrichedParticipantDetails, setEnrichedParticipantDetails] = useState<Record<string, { name: string; avatar?: string }>>({});

  // Enrich participant names from users collection (fixes super_admin name+surname for mention display)
  useEffect(() => {
    if (!conversation || conversation.type !== 'group' || conversation.participantIds.length === 0) {
      setEnrichedParticipantDetails({});
      return;
    }
    const participantIds = conversation.participantIds;
    const participantDetails = conversation.participantDetails;
    let cancelled = false;
    chatService.getUserDetailsBatch(participantIds).then((userDetails) => {
      if (cancelled) return;
      const enriched: Record<string, { name: string; avatar?: string }> = {};
      participantIds.forEach((userId) => {
        const detail = userDetails[userId];
        const existing = participantDetails[userId];
        const fullName = detail
          ? [detail.name, detail.surname].filter(Boolean).join(' ').trim() || detail.name || 'Unknown'
          : existing?.name || 'Unknown';
        enriched[userId] = {
          name: fullName,
          avatar: existing?.avatar,
        };
      });
      setEnrichedParticipantDetails(enriched);
    });
    return () => {
      cancelled = true;
    };
  }, [conversation?.id, conversation?.participantIds?.join(','), conversation?.type]);

  // Check if user is near the bottom of the scroll container
  const isNearBottom = (container: HTMLDivElement | null, threshold: number = 100): boolean => {
    if (!container) return false;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  // Auto-scroll to bottom only when new messages arrive AND user is already near bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !messagesEndRef.current || isLoading) return;

    const currentMessageCount = messages.length;
    const previousMessageCount = previousMessageCountRef.current;
    const hasNewMessages = currentMessageCount > previousMessageCount;
    
    // If this is the first load (previous count was 0), always scroll to bottom
    const isFirstLoad = previousMessageCount === 0 && currentMessageCount > 0;
    
    // Update previous count
    previousMessageCountRef.current = currentMessageCount;

    // Auto-scroll if:
    // 1. First load (initial messages) - always scroll to bottom
    // 2. OR new messages were added AND user is already near the bottom AND not scrolling
    if (isFirstLoad) {
      // Use requestAnimationFrame for smooth initial scroll
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(() => {
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
            }
          }, 100);
        });
      } else {
        // Fallback for environments without requestAnimationFrame
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
        }, 100);
      }
    } else if (hasNewMessages && isNearBottom(container, 100) && !isUserScrollingRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Handle jump to message event (from search results)
  useEffect(() => {
    let retryInterval: NodeJS.Timeout | null = null;

    const scrollToMessage = (messageId: string): void => {
      const messageElement = document.getElementById(`message-${messageId}`);
      
      if (messageElement) {
        // Scroll to message with smooth behavior
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight the message briefly (4 seconds - double the previous 2 seconds)
        messageElement.style.backgroundColor = '#fffbe6';
        setTimeout(() => {
          messageElement.style.backgroundColor = '';
        }, 4000);
      }
    };

    const handleJumpToMessage = async (e: globalThis.Event) => {
      const customEvent = e as globalThis.CustomEvent<{ messageId: string; messageDate?: Date }>;
      const messageId = customEvent.detail?.messageId;
      const messageDate = customEvent.detail?.messageDate;
      
      if (!messageId || !messagesContainerRef.current || !conversation) return;

      // Clear any existing retry interval
      if (retryInterval) {
        clearInterval(retryInterval);
        retryInterval = null;
      }

      // Check if the message exists in the current messages array
      const messageExists = messages.some((msg) => msg.id === messageId);
      
      // If message exists, scroll immediately
      if (messageExists) {
        setTimeout(() => {
          scrollToMessage(messageId);
        }, 100);
        return;
      }

      // Message doesn't exist - if we have a date, load messages around that date
      if (messageDate) {
        try {
          setIsLoadingMore(true);
          
          // Load messages around the target date (25 before, 25 after)
          const messagesAroundDate = await chatService.getMessagesAroundDate(
            conversation.id,
            messageDate,
            25,
            25
          );

          if (messagesAroundDate.length > 0) {
            // Check if the target message is in the loaded messages
            const targetMessage = messagesAroundDate.find(msg => msg.id === messageId);
            
            if (targetMessage) {
              // Prepend messages around the target date to provide context
              // The prependMessages function handles deduplication
              prependMessages(conversation.id, messagesAroundDate);
              
              // Wait for messages to render, then scroll
              // Use a longer timeout to ensure messages are rendered
              setTimeout(() => {
                scrollToMessage(messageId);
              }, 500);
            } else {
              // Message not found even after loading around date - fall back to retry
              console.warn(`Message ${messageId} not found even after loading messages around date`);
              startRetryLogic(messageId);
            }
          } else {
            // No messages found around date - fall back to retry
            startRetryLogic(messageId);
          }
        } catch (error) {
          console.error('Failed to load messages around date:', error);
          // Fall back to retry logic
          startRetryLogic(messageId);
        } finally {
          setIsLoadingMore(false);
        }
      } else {
        // No date provided - use retry logic (for backward compatibility)
        startRetryLogic(messageId);
      }

      function startRetryLogic(msgId: string) {
        // Message doesn't exist yet (common for group chats), wait and retry
        let retryCount = 0;
        const maxRetries = 25; // Try for up to 5 seconds (25 * 200ms)
        
        retryInterval = setInterval(() => {
          retryCount++;
          
          // Check again if message exists
          const nowExists = messages.some((msg) => msg.id === msgId);
          
          if (nowExists) {
            // Message is now loaded, scroll to it
            clearInterval(retryInterval!);
            retryInterval = null;
            
            setTimeout(() => {
              scrollToMessage(msgId);
            }, 100);
          } else if (retryCount >= maxRetries) {
            // Max retries reached, give up
            clearInterval(retryInterval!);
            retryInterval = null;
            console.warn(`Could not find message ${msgId} after ${maxRetries} retries`);
          }
        }, 200); // Check every 200ms
      }
    };

    window.addEventListener('jumpToMessage', handleJumpToMessage);
    return () => {
      window.removeEventListener('jumpToMessage', handleJumpToMessage);
      if (retryInterval) {
        clearInterval(retryInterval);
      }
    };
  }, [messages, conversation, prependMessages]); // Dependencies: messages, conversation, and prependMessages

  // Auto-scroll to bottom when conversation changes (initial load or switching conversations)
  useEffect(() => {
    if (!conversation || isLoading) return;
    
    // Wait for messages to be rendered before scrolling
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully rendered
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        setTimeout(scrollToBottom, 50);
      });
    } else {
      // Fallback for environments without requestAnimationFrame
      setTimeout(scrollToBottom, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, isLoading]);

  // Track user scroll behavior
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      // Mark that user is scrolling
      isUserScrollingRef.current = true;

      // Clear existing timeout
      clearTimeout(scrollTimeout);

      // Reset scrolling flag after user stops scrolling for 500ms
      scrollTimeout = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 500);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Check if there are more messages to load when conversation or messages change
  useEffect(() => {
    if (!conversation || messages.length === 0) {
      setHasMoreMessages(true);
      return;
    }

    // If we have less than 50 messages, there are likely no more to load
    // (since the snapshot loads 50, if we have exactly 50, there might be more)
    // We'll check this when user tries to load more
    if (messages.length < 50) {
      setHasMoreMessages(false);
    } else {
      setHasMoreMessages(true);
    }
  }, [conversation, messages.length]);

  // Load more messages (older messages)
  const loadMoreMessages = async () => {
    if (!conversation || isLoadingMore || !hasMoreMessages) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    // Save current scroll position
    scrollPositionRef.current = container.scrollHeight - container.scrollTop;

    setIsLoadingMore(true);

    try {
      // Get the oldest message date
      const oldestMessage = messages[0];
      if (!oldestMessage) {
        setHasMoreMessages(false);
        return;
      }

      // Load older messages
      const olderMessages = await chatService.getMessages(
        conversation.id,
        50,
        oldestMessage.createdAt
      );

      if (olderMessages.length === 0) {
        // No more messages to load
        setHasMoreMessages(false);
      } else {
        // Prepend older messages to the store
        prependMessages(conversation.id, olderMessages);

        // If we got less than 50 messages, there are no more to load
        if (olderMessages.length < 50) {
          setHasMoreMessages(false);
        }

        // Restore scroll position after messages are rendered
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - scrollPositionRef.current;
          }
        }, 0);
      }
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Mark messages as read when viewing conversation
  useEffect(() => {
    if (!conversation || !messages.length) return;

    // Find unread messages (messages not read by current user)
    const unreadMessages = messages.filter(
      (msg) => msg.senderId !== currentUserId && !msg.readBy[currentUserId]
    );

    if (unreadMessages.length > 0) {
      const messageIds = unreadMessages.map(msg => msg.id);
      markAsRead(conversation.id, messageIds, currentUserId);
    }
  }, [conversation, messages, currentUserId, markAsRead]);

  if (!conversation) {
    return (
      <ChatContainer>
        <EmptyState>
          <Empty
            description="Select a conversation to start chatting"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </EmptyState>
      </ChatContainer>
    );
  }

  const getConversationName = () => {
    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }

    // For direct chats, show the other participant's name
    const otherParticipantId = conversation.participantIds.find(
      (id) => id !== currentUserId
    );

    if (otherParticipantId) {
      const participant = conversation.participantDetails[otherParticipantId];
      return participant?.name || 'Unknown User';
    }

    return 'Chat';
  };

  const getAvatar = () => {
    if (conversation.type === 'group') {
      return conversation.avatar ? (
        <HeaderAvatar src={conversation.avatar} $isMobile={isMobile} />
      ) : (
        <HeaderAvatar icon={<TeamOutlined />} $bgColor="#1890ff" $isMobile={isMobile} />
      );
    }

    const otherParticipantId = conversation.participantIds.find(
      (id) => id !== currentUserId
    );

    if (otherParticipantId) {
      const participant = conversation.participantDetails[otherParticipantId];
      const initials = getInitials(participant?.name || 'Unknown');
      const color = getAvatarColor(participant?.name || 'Unknown');
      
      return (
        <HeaderAvatar 
          src={participant?.avatar} 
          $bgColor={participant?.avatar ? undefined : color}
          $isMobile={isMobile}
        >
          {!participant?.avatar && initials}
        </HeaderAvatar>
      );
    }

    return <HeaderAvatar $bgColor="#999" $isMobile={isMobile}>?</HeaderAvatar>;
  };

  const getTypingIndicatorText = () => {
    if (conversation.typingUsers.length === 0) return null;

    const typingUserNames = conversation.typingUsers
      .filter((userId) => userId !== currentUserId)
      .map((userId) => {
        const participant = conversation.participantDetails[userId];
        return participant?.name || 'Someone';
      });

    if (typingUserNames.length === 0) return null;

    if (typingUserNames.length === 1) {
      return `${typingUserNames[0]} is typing...`;
    }

    if (typingUserNames.length === 2) {
      return `${typingUserNames[0]} and ${typingUserNames[1]} are typing...`;
    }

    return `${typingUserNames.length} people are typing...`;
  };

  // Create dropdown menu items for mobile
  const getMobileMenuItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = [];
    
    if (conversation.type === 'group') {
      items.push({
        key: 'rename',
        label: t('renameGroupChat.title') || 'Rename group chat',
        icon: <EditOutlined />,
        onClick: () => setRenameModalOpen(true),
      });
      
      items.push({
        key: 'addMembers',
        label: t('addMembersModal.title') || 'Add Members',
        icon: <UserAddOutlined />,
        onClick: () => setAddMembersModalOpen(true),
      });
    }
    
    if (conversation.type === 'direct' || conversation.createdBy === currentUserId) {
      items.push({
        key: 'delete',
        label: conversation.type === 'group' 
          ? (t('deleteChat.titleGroup') || 'Delete group chat')
          : (t('deleteChat.titleDirect') || 'Delete chat'),
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => setDeleteModalOpen(true),
      });
    }
    
    return items;
  };

  return (
    <ChatContainer>
      <ChatHeader $isMobile={isMobile}>
        <HeaderLeft>
          {isMobile && onBack && (
            <BackButton
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              aria-label="Back to conversations"
            />
          )}
          {!isMobile && getAvatar()}
          <HeaderContent $isMobile={isMobile}>
            <ConversationNameContainer>
              <ConversationName level={5} $isMobile={isMobile}>{getConversationName()}</ConversationName>
              {conversation.type === 'group' && !isMobile && !isTablet && (
                <EditIconButton
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => setRenameModalOpen(true)}
                  title={t('renameGroupChat.title') || 'Rename group chat'}
                  $isMobile={isMobile}
                />
              )}
            </ConversationNameContainer>
            {conversation.type === 'group' ? (
              <ClickableParticipantCount onClick={() => setParticipantsModalOpen(true)} $isMobile={isMobile}>
                {conversation.participantIds.length} {t('participants')}
              </ClickableParticipantCount>
            ) : (
              <OnlineStatus $isMobile={isMobile}>Online</OnlineStatus>
            )}
          </HeaderContent>
        </HeaderLeft>
        <HeaderRight $isMobile={isMobile}>
          {isMobile || isTablet ? (
            <Dropdown
              menu={{ items: getMobileMenuItems() }}
              placement="bottomRight"
              trigger={['click']}
            >
              <MobileMenuButton
                type="text"
                icon={<MoreOutlined />}
                aria-label="More options"
              />
            </Dropdown>
          ) : (
            <>
              {conversation.type === 'group' && (
                <Button
                  type="text"
                  icon={<UserAddOutlined />}
                  onClick={() => setAddMembersModalOpen(true)}
                  size="small"
                >
                  {t('addMembersModal.title') || 'Add Members'}
                </Button>
              )}
              {/* Only show delete button for group chats if user is creator, or for direct chats (both participants can delete) */}
              {(conversation.type === 'direct' || conversation.createdBy === currentUserId) && (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteModalOpen(true)}
                  size="small"
                  danger
                  title={conversation.type === 'group' 
                    ? t('deleteChat.titleGroup') || 'Delete group chat'
                    : t('deleteChat.titleDirect') || 'Delete chat'}
                >
                  {t('deleteChat.button') || 'Delete'}
                </Button>
              )}
            </>
          )}
        </HeaderRight>
      </ChatHeader>

      <MessagesContainer ref={messagesContainerRef} $isTablet={isTablet} $isMobile={isMobile}>
        {isLoading ? (
          <EmptyState>
            <Spin size="large" />
          </EmptyState>
        ) : messages.length === 0 ? (
          <EmptyState>
            <Empty description="No messages yet. Start the conversation!" />
          </EmptyState>
        ) : (
          <>
            {/* Load More Button */}
            {hasMoreMessages && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Button
                  type="default"
                  loading={isLoadingMore}
                  onClick={loadMoreMessages}
                  disabled={isLoadingMore}
                >
                  {t('chat.loadMoreMessages') || 'Load Previous Messages'}
                </Button>
              </div>
            )}
            {messages.map((message, index) => {
              const isOwnMessage = message.senderId === currentUserId;
              // Always show avatar for all messages
              const showAvatar = true;

              // Check if we need to show a date divider
              const showDateDivider =
                index === 0 ||
                !isSameDay(message.createdAt, messages[index - 1].createdAt);

              return (
                <React.Fragment key={message.id}>
                  {showDateDivider && (
                    <DateDivider>
                      <DateDividerText>
                        {format(message.createdAt, 'dd/MM/yyyy')}
                      </DateDividerText>
                    </DateDivider>
                  )}
                  <MessageItem
                    message={message}
                    isOwnMessage={isOwnMessage}
                    showAvatar={showAvatar}
                    userId={currentUserId}
                    participantDetails={
                      Object.keys(enrichedParticipantDetails).length > 0
                        ? enrichedParticipantDetails
                        : conversation.participantDetails
                    }
                    conversationType={conversation.type}
                    messages={messages}
                    onReactionClick={async (emoji: string) => {
                      // Add or change reaction
                      await reactToMessage(conversation.id, message.id, currentUserId, emoji);
                    }}
                    onRemoveReaction={async () => {
                      // Remove reaction
                      await removeReaction(conversation.id, message.id, currentUserId);
                    }}
                    onEdit={(message) => {
                      setMessageToEdit(message);
                      setEditModalOpen(true);
                    }}
                    onReply={(message) => {
                      // Don't allow replying to deleted or system messages
                      if (message.deletedBy.length > 0 || message.type === 'system') {
                        return;
                      }
                      setReplyingTo(message);
                    }}
                  />
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>

      {getTypingIndicatorText() && (
        <TypingIndicator>
          {getTypingIndicatorText()}
        </TypingIndicator>
      )}

      <MessageInput
        onSendMessage={(content, mentions, replyTo) => {
          onSendMessage(content, mentions, replyTo);
          setReplyingTo(null); // Clear reply after sending
        }}
        onTyping={onTyping}
        disabled={!conversation}
        conversation={conversation}
        currentUserId={currentUserId}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
      />

      {conversation && (
        <>
          <ParticipantsModal
            open={participantsModalOpen}
            conversation={conversation}
            currentUserId={currentUserId}
            onClose={() => setParticipantsModalOpen(false)}
          />
          <AddMembersModal
            key={conversation?.id} // Force remount when conversation changes
            open={addMembersModalOpen}
            conversation={conversation}
            currentUserId={currentUserId}
            scope={scope}
            municipalityId={municipalityId}
            onClose={() => setAddMembersModalOpen(false)}
            onMembersAdded={() => {
              // Conversation will update via real-time listener
            }}
          />
          <RenameGroupChatModal
            open={renameModalOpen}
            conversation={conversation}
            onClose={() => setRenameModalOpen(false)}
            onRename={async (conversationId, newName) => {
              await renameGroupChat(conversationId, newName, currentUserId);
            }}
          />
          <DeleteChatConfirmationModal
            open={deleteModalOpen}
            conversation={conversation}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={async (conversationId) => {
              await deleteConversation(conversationId, currentUserId);
            }}
          />
          <EditMessageModal
            open={editModalOpen}
            message={messageToEdit}
            onClose={() => {
              setEditModalOpen(false);
              setMessageToEdit(null);
            }}
            onConfirm={async (messageId, newContent) => {
              if (conversation) {
                await editMessage(conversation.id, messageId, newContent, currentUserId);
              }
            }}
          />
        </>
      )}
    </ChatContainer>
  );
};

